import { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row, Stack } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ExamStepEnum } from 'src/services/offline/db';
import {
  setOrderLastStepIDB,
  updateExamFieldsIDB,
} from 'src/services/offline/outboxClient';
import {
  captureExamEvent,
  EXAM_EVENTS,
  EXAM_STEP,
  secondsSince,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  CustomModal,
  CustomModalBody,
  CustomModalFooter,
  CustomModalHeader,
  CustomModalTitle,
} from 'src/components/custom_components/CustomModal';

import {
  DiabetesDataLabelEnum,
  LengthOfDiabetesDiagnosisEnum,
  TimeSinceLastEyeExamEnum,
  TypeOfDiabetesEnum,
} from 'src/models/DiabetesData.model';
import { I18NextNS } from 'src/models/Language.model';

const DiabetesData = () => {
  const { t } = useTranslation(I18NextNS.PAGES);
  const history = useHistory();

  const { session, updateSession } = useRemoteBoundStore();

  useEffect(() => {
    if (!session.orderId) {
      return;
    }
    setOrderLastStepIDB(session.orderId, ExamStepEnum.DIABETES_DATA);
  }, [session.orderId]);

  const stepStartedAtRef = useRef(performance.now());

  const { handleSubmit, register } = useForm({
    defaultValues: {
      typeOfDiabetes: TypeOfDiabetesEnum.Other,
      lengthOfDiabetesDiagnosis: LengthOfDiabetesDiagnosisEnum.Unknown,
      timeSinceLastEyeExam: TimeSinceLastEyeExamEnum.Unknown,
    },
  });

  const submitDiabetesData: SubmitHandler<{
    typeOfDiabetes: TypeOfDiabetesEnum;
    lengthOfDiabetesDiagnosis: LengthOfDiabetesDiagnosisEnum;
    timeSinceLastEyeExam: TimeSinceLastEyeExamEnum;
  }> = async (data) => {
    if (!session.orderId) {
      throw new Error(t('message.error.orderIdNotFound'));
    }
    updateSession({
      typeOfDiabetes: data.typeOfDiabetes,
      lengthOfDiabetesDiagnosis: data.lengthOfDiabetesDiagnosis,
      timeSinceLastEyeExam: data.timeSinceLastEyeExam,
    });
    updateExamFieldsIDB(session.orderId, session.iid, {
      TypeOfDiabetes: data.typeOfDiabetes,
      LengthOfDiabetesDiagnosis: data.lengthOfDiabetesDiagnosis,
      TimeSinceLastEyeExam: data.timeSinceLastEyeExam,
    });

    captureExamEvent(EXAM_EVENTS.DIABETIC_DATA_COLLECTED, {
      step: EXAM_STEP.DIABETES_DATA,
      step_duration_seconds: secondsSince(stepStartedAtRef.current),
    });

    setIsShowConfirmModal(true);
  };

  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);

  const handleCloseConfirmModal = () => {
    setIsShowConfirmModal(false);
  };

  return (
    <Stack gap={4} className="h-100">
      <Row>
        <Col md={6}>
          <div>
            <p className="mb-0">{t('diabetesData.title')}</p>
            <h3 data-test="patient-name">
              {session.firstName} {session.lastName}
            </h3>
          </div>
        </Col>
      </Row>
      <Row className="h-100">
        <Form
          className="d-flex flex-column justify-content-between"
          onSubmit={handleSubmit(submitDiabetesData)}
        >
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label data-test="type-of-diabetes-label">
                  {DiabetesDataLabelEnum.TypeOfDiabetes}
                </Form.Label>
                <Form.Select
                  data-test="type-of-diabetes-select"
                  {...register('typeOfDiabetes')}
                >
                  {Object.values(TypeOfDiabetesEnum).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label data-test="length-of-diagnosis-label">
                  {DiabetesDataLabelEnum.LengthOfDiabetesDiagnosis}
                </Form.Label>
                <Form.Select
                  data-test="length-of-diagnosis-select"
                  {...register('lengthOfDiabetesDiagnosis')}
                >
                  {Object.values(LengthOfDiabetesDiagnosisEnum).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label data-test="time-since-last-exam-label">
                  {DiabetesDataLabelEnum.TimeSinceLastEyeExam}
                </Form.Label>
                <Form.Select
                  data-test="time-since-last-exam-select"
                  {...register('timeSinceLastEyeExam')}
                >
                  {Object.values(TimeSinceLastEyeExamEnum).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Stack
            direction="horizontal"
            className="justify-content-end mb-5"
            gap={2}
          >
            <Button
              variant="primary-100"
              onClick={() => history.goBack()}
              data-test="previous-button"
              data-track="exam.diabetes-data.previous"
              data-observe
            >
              {t('diabetesData.navigation.previous')}
            </Button>

            <Button
              variant="primary"
              className="w-200px shadow"
              type="submit"
              data-test="diabetes-next-button"
              data-track="exam.diabetes-data.next"
              data-observe
            >
              {t('diabetesData.navigation.next')}
            </Button>
          </Stack>
        </Form>
      </Row>

      <CustomModal
        closeButton
        show={isShowConfirmModal}
        onHide={handleCloseConfirmModal}
        scrollable={true}
        fullscreen="xl-down"
        backdrop="static"
      >
        <CustomModalHeader>
          <CustomModalTitle data-test="diabetes-confirm-modal-title">
            {t('diabetesData.confirmModal.title')}
          </CustomModalTitle>
        </CustomModalHeader>
        <CustomModalBody>
          <Card>
            <Card.Body>
              <Stack gap={2}>
                <Row>
                  <Col>
                    <span className="fw-medium">
                      {DiabetesDataLabelEnum.TypeOfDiabetes}
                    </span>
                  </Col>
                  <Col>
                    <span data-test="confirm-type-of-diabetes-value">
                      {session.typeOfDiabetes}
                    </span>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span className="fw-medium">
                      {DiabetesDataLabelEnum.LengthOfDiabetesDiagnosis}
                    </span>
                  </Col>
                  <Col>
                    <span data-test="confirm-length-of-diagnosis-value">
                      {session.lengthOfDiabetesDiagnosis}
                    </span>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span className="fw-medium">
                      {DiabetesDataLabelEnum.TimeSinceLastEyeExam}
                    </span>
                  </Col>
                  <Col>
                    <span data-test="confirm-time-since-last-exam-value">
                      {session.timeSinceLastEyeExam}
                    </span>
                  </Col>
                </Row>
              </Stack>
            </Card.Body>
          </Card>
        </CustomModalBody>

        <CustomModalFooter>
          <Button
            variant="primary-100"
            onClick={handleCloseConfirmModal}
            data-track="exam.diabetes-data-confirm-modal.cancel"
            data-test="diabetes-cancel-button"
            data-observe
          >
            {t('diabetesData.confirmModal.cancel')}
          </Button>

          <Button
            variant="primary"
            className="shadow"
            onClick={() => history.push('/session-hint')}
            data-test="diabetes-confirm-button"
            data-track="exam.diabetes-data-confirm-modal.confirm"
            data-observe
          >
            {t('diabetesData.confirmModal.confirm')}
          </Button>
        </CustomModalFooter>
      </CustomModal>
    </Stack>
  );
};

export default DiabetesData;
